import type {
  CustomerCreditRuntimeItem,
  CustomerOpenReceivableRuntimeItem,
} from "../gateway/local-projection-client";
import type { CustomerCreditSummaryReadback } from "../gateway/receivable-settlement-client";
import { indexedScope } from "./indexed-runtime";
import { posRuntimeDb } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

export function normalizeOpenReceivables(value: Array<Record<string, unknown>>): CustomerOpenReceivableRuntimeItem[] {
  return value.flatMap((raw) => {
    const status = String(raw.receivable_status ?? "");
    if (status !== "OPEN" && status !== "PARTIALLY_PAID") return [];
    const receivableId = String(raw.receivable_id ?? "");
    const sourceOrderId = String(raw.source_order_id ?? "");
    if (!receivableId || !sourceOrderId) return [];
    return [{
      receivable_id: receivableId,
      receivable_no: String(raw.receivable_no ?? ""),
      source_order_id: sourceOrderId,
      order_no: raw.order_no == null ? null : String(raw.order_no),
      branch_id: String(raw.branch_id ?? ""),
      branch_code: raw.branch_code == null ? null : String(raw.branch_code),
      branch_name: raw.branch_name == null ? null : String(raw.branch_name),
      business_date: raw.business_date == null ? null : String(raw.business_date),
      sale_occurred_at: raw.sale_occurred_at == null ? null : String(raw.sale_occurred_at),
      created_at: String(raw.created_at ?? ""),
      due_date: raw.due_date == null ? null : String(raw.due_date),
      currency_code: String(raw.currency_code ?? "THB"),
      original_amount: Number(raw.original_amount ?? 0),
      allocated_amount: Number(raw.allocated_amount ?? 0),
      remaining_amount: Number(raw.remaining_amount ?? raw.original_amount ?? 0),
      receivable_status: status,
    } satisfies CustomerOpenReceivableRuntimeItem];
  });
}

export async function applyCanonicalCustomerCreditSummaryReadback(
  identity: TrustedTerminalIdentity,
  summary: CustomerCreditSummaryReadback,
  changedAt: string,
) {
  const customerId = summary.customer_id;
  const scopeKey = indexedScope(identity, "customer-credit");
  const row = await posRuntimeDb.customerCredits
    .where("customerId")
    .equals(customerId)
    .and((candidate) => candidate.scopeKey === scopeKey)
    .first();
  if (!row?.payload || typeof row.payload !== "object") return;

  const current = row.payload as CustomerCreditRuntimeItem;
  const openReceivables = normalizeOpenReceivables(summary.open_receivables ?? []);
  const effectiveChangedAt = changedAt || new Date().toISOString();
  const next: CustomerCreditRuntimeItem = {
    ...current,
    credit_limit: summary.credit_limit == null ? current.credit_limit : Number(summary.credit_limit),
    currency_code: summary.currency_code ?? current.currency_code,
    current_outstanding: Number(summary.current_outstanding ?? 0),
    available_credit: summary.available_credit == null ? null : Number(summary.available_credit),
    open_receivable_count: Number(summary.open_receivable_count ?? openReceivables.length),
    open_receivables: openReceivables,
    changed_at: effectiveChangedAt,
  };

  await posRuntimeDb.customerCredits.put({
    ...row,
    creditLimit: next.credit_limit,
    currencyCode: next.currency_code,
    changedAt: effectiveChangedAt,
    payload: next,
  });
}